import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { memo, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import ReactModal from "react-modal";

import { ReactComponent as CheckIcon } from "assets/icons/connection/check.svg";
import { ReactComponent as ConnectingIcon } from "assets/icons/connection/connecting.svg";
import { ReactComponent as FailIcon } from "assets/icons/connection/fail.svg";
import Button from "components/Button";
import Label from "components/Label";
import PasswordTextField from "components/PasswordTextField";
import Select from "components/Select";
import TextField from "components/TextField";
import { useGetGrades } from "hooks/apis/grade";
import { useNewUser, useUpdateUser } from "hooks/apis/user";
import { User } from "types/types";
import { bankOptions } from "utils/const";
import SimpleModal from "./SimpleModal";

type FormValues = {
  name: string;
  userId: string;
  password?: string;
  passwordConfirm?: string;
  phone: string;
  residentId1: string;
  residentId2: string;
  daesungId: string;
  daesungPassword: string;
  bank: string;
  bankNumber: string;
  grade: number;
};

const AddAssistant = (props: User | Record<string, never>) => {
  const modal = useModal();
  const methods = useForm<FormValues>({
    defaultValues: { grade: 6 },
    mode: "onChange",
  });
  const password = methods.watch("password");

  const newUserMutation = useNewUser();
  const updateUserMutation = useUpdateUser();

  const isEditing = "id" in props;

  const { data: grades } = useGetGrades();

  const gradeOptions = useMemo(
    () => [
      ...(grades || []).map((grade) => ({
        label: grade.name,
        value: `${grade.id}`,
      })),
    ],
    [grades],
  );

  useEffect(() => {
    if (isEditing) {
      methods.reset({
        name: props.name,
        userId: props.userId,
        phone: props.phone,
        residentId1: props.residentId1,
        residentId2: props.residentId2,
        daesungId: props.daesungId,
        bank: props.bank,
        bankNumber: props.bankNumber,
        grade: props.grade.id,
      });
    }
  }, []);

  const closeModal = () => {
    modal.hide();
    modal.reject();
  };

  const onSubmit = (form: FormValues) => {
    if (isEditing) {
      updateUserMutation.mutate(
        { ...form, id: props.id },
        {
          onSuccess: () => {
            NiceModal.show(SimpleModal, {
              message: "정보 수정이 완료되었습니다.",
              showCancel: false,
            }).then(closeModal);
          },
        },
      );
    } else {
      // @ts-expect-error
      newUserMutation.mutate(form);
    }
  };

  const onCancel = () => {
    if (isEditing) {
      updateUserMutation.reset();
    } else {
      newUserMutation.reset();
    }
  };

  const onPrevious = () => {
    if (isEditing) {
      updateUserMutation.reset();
    } else {
      newUserMutation.reset();
    }
  };

  const getContent = () => {
    if (newUserMutation.isSuccess) {
      return (
        <>
          <h2 className="text-24 leading-[1.375rem] text-center font-bold mb-[1.875rem]">
            계정 연동 성공
          </h2>
          <div className="mt-[4.938rem] flex flex-col items-center flex-1">
            <CheckIcon />
            <p className="mt-[-0.313rem] text-center whitespace-pre-wrap font-medium text-18 text-secondary">
              {"계정이 정상적으로\n연동 및 생성되었습니다!"}
            </p>
          </div>
          <Button onClick={closeModal}>확인</Button>
        </>
      );
    }

    if (newUserMutation.isError) {
      return (
        <>
          <h2 className="text-24 leading-[1.375rem] text-center font-bold mb-[1.875rem]">
            계정 연동 실패
          </h2>
          <div className="mt-[5.625rem] flex flex-col items-center flex-1">
            <FailIcon />
            <p className="mt-[0.625rem] text-center whitespace-pre-wrap font-medium text-18 text-secondary">
              {"계정 연동에  실패하였습니다.\n다시 시도해주세요."}
            </p>
          </div>
          <Button outline onClick={onPrevious}>
            이전으로 돌아가기
          </Button>
        </>
      );
    }

    if (newUserMutation.isPending) {
      return (
        <>
          <h2 className="text-24 leading-[1.375rem] text-center font-bold mb-[1.875rem]">
            계정 연동중
          </h2>
          <div className="mt-[6.75rem] flex flex-col items-center flex-1">
            <ConnectingIcon />
            <p className="mt-10 text-center whitespace-pre-wrap font-medium text-18 text-secondary">
              {"계정을 연동중입니다.\n잠시만 기다려주세요"}
            </p>
          </div>
          <Button outline onClick={onCancel}>
            취소
          </Button>
        </>
      );
    }
    return (
      <>
        <h2 className="text-24 leading-[1.375rem] text-center font-bold mb-[1.875rem]">
          {isEditing ? "정보 수정" : "계정 생성"}
        </h2>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="space-y-[0.938rem]"
        >
          <TextField
            name="name"
            label={isEditing ? "이름" : "이름*"}
            maxLength={15}
            pattern={/[^\u3131-\u318E\uAC00-\uD7A3a-zA-Z]/g}
          />
          <TextField
            name="userId"
            required={!isEditing}
            label={isEditing ? "아이디" : "아이디*"}
            readOnly={isEditing}
            rules={
              isEditing
                ? undefined
                : {
                    minLength: {
                      value: 4,
                      message: "최소 4자 이상 입력해 주세요.",
                    },
                  }
            }
            pattern={/[^a-zA-Z0-9]/g}
            maxLength={20}
          />
          <div className="space-y-[0.313rem]">
            <TextField
              name="password"
              required={!isEditing}
              type="password"
              label={isEditing ? undefined : "비밀번호*"}
              placeholder={isEditing ? "새 비밀번호 " : "비밀번호"}
              rules={
                isEditing && !password
                  ? undefined
                  : {
                      pattern: {
                        value:
                          // eslint-disable-next-line no-useless-escape
                          /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]{8,20}$/,
                        message:
                          "대/소문자 영문 + 숫자 + 특수기호 조합으로 8~20자 이내",
                      },
                    }
              }
              maxLength={20}
            />
            <TextField
              name="passwordConfirm"
              type="password"
              required={!isEditing || !!password}
              placeholder={isEditing ? "새 비밀번호 확인" : "비밀번호 확인"}
              rules={{
                validate: (value) =>
                  !value ||
                  value === password ||
                  "비밀번호가 일치하지 않습니다.",
              }}
              maxLength={20}
            />
          </div>
          <TextField
            name="phone"
            required
            label={isEditing ? "휴대전화번호" : "휴대전화번호*"}
            number
            maxLength={11}
            rules={{
              minLength: 11,
            }}
          />
          <div className="flex items-end gap-[0.531rem]">
            <TextField
              name="residentId1"
              required
              number
              label={isEditing ? "주민등록번호" : "주민등록번호*"}
              className="flex-1"
              showError={false}
              maxLength={6}
              rules={{
                minLength: 6,
              }}
            />
            <TextField
              type="password"
              name="residentId2"
              required
              number
              className="flex-1"
              showError={false}
              rules={{
                minLength: 7,
              }}
              maxLength={7}
            />
          </div>
          <div className="space-y-[0.313rem]">
            <Label>{`대성 아이디/비밀번호${isEditing ? "" : "*"}`}</Label>
            <TextField name="daesungId" maxLength={50} />
            <PasswordTextField
              name="daesungPassword"
              required={!isEditing}
              maxLength={50}
            />
          </div>
          <div>
            <Label>{`등급${isEditing ? "" : "*"}`}</Label>
            <div className="flex gap-[0.313rem]">
              <Select name="grade" options={gradeOptions} border size="big" />
            </div>
          </div>
          <div>
            <Label>{`은행/계좌번호${isEditing ? "" : "*"}`}</Label>
            <div className="flex gap-[0.313rem]">
              <Select
                name="bank"
                options={bankOptions}
                border
                size="big"
                required
                className="!w-[7.188rem]"
              />
              <TextField
                required
                name="bankNumber"
                number
                className="flex-1"
                maxLength={35}
              />
            </div>
          </div>
          <div className="flex gap-2 !mt-[1.875rem]">
            <Button outline className="flex-1" onClick={closeModal}>
              취소
            </Button>
            <Button type="submit" className="flex-1">
              {isEditing ? "정보 수정" : "다음"}
            </Button>
          </div>
        </form>
      </>
    );
  };

  return (
    <ReactModal
      overlayClassName="side flex justify-end items-start fixed h-full top-0 left-0 right-0 duration-300 z-30 bg-[rgba(0,0,0,0.2)]"
      className="translate-x-full bg-[rgba(255,255,255,0.9)] flex flex-col w-[25rem] h-full pt-[6rem] pb-10 px-[1.75rem] outline-none overflow-auto"
      isOpen={modal.visible}
      closeTimeoutMS={300}
      onRequestClose={closeModal}
      onAfterClose={() => {
        modal.remove();
      }}
    >
      <FormProvider {...methods}>{getContent()}</FormProvider>
    </ReactModal>
  );
};

const AddAssistantModal = NiceModal.create(memo(AddAssistant));
AddAssistantModal.displayName = "AddAssistantModal";

export default AddAssistantModal;
